import React from "react";
import { NavigateFunction } from "react-router-dom";
import PaymentIcon from "../../../../../images/membership/dollar-blue.svg";
import UpsertPaymentPopUp from "./UpsertPaymentPopUp";
import {
  ContactReferenceModel,
  CountryWithStateModel,
  RelatedPaymentsGridModel,
  UpsertPaymentModel,
} from "../models";
import {
  GridNames,
  NewPaymentRelatedSource,
  PaymentTransactionStatus,
} from "../enums";
import {
  AuthService,
  CustomButton,
  ITableHeaders,
  TableWithHeaderButtons,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { PaymentUtil } from "../utils";
import RenderPaymentMethodType from "./RenderPaymentMethodType";
import RenderPaymentMethod from "./RenderPaymentMethod";
import PlaceholderSmallerGrid from "./PlaceholderSmallerGrid";
import TableGrid from "./TableGrid";
import { AccountSettingsStore, RolesAndPermissionStore } from "../store";

type IProps = {
  navigate: NavigateFunction;
  payments: RelatedPaymentsGridModel[];
  onUpdate: (contactId?: string) => void;
  contact: ContactReferenceModel;
  source: keyof typeof NewPaymentRelatedSource;
  sourceId: string; //id of contact, subscription, invoice, checkout, purchaselinks
  rolesAndPermissionStore: RolesAndPermissionStore;
  disableNewButton?: boolean;
  countriesWithState: CountryWithStateModel[];
};
const RelatedPaymentsGrid: React.FC<IProps> = ({
  payments,
  navigate,
  onUpdate,
  contact,
  source,
  sourceId,
  rolesAndPermissionStore,
  disableNewButton,
  countriesWithState,
}) => {
  const [upsertPaymentDetails, setPaymentDetails] =
    React.useState<UpsertPaymentModel | null>(null);

  const headers = () => {
    const columns: ITableHeaders<RelatedPaymentsGridModel>[] = [
      {
        dataKey: "status",
        title: "Status",
        onCellRender: (data: RelatedPaymentsGridModel) => {
          const colors = PaymentUtil.getColorsByStatus(data.status);
          return (
            <CustomButton color={colors.color} bgColor={colors.bgColor}>
              {PaymentTransactionStatus[data.status]}
            </CustomButton>
          );
        },
      },
      {
        dataKey: "createdDate",
        title: "Date",
        onCellRender: (data: RelatedPaymentsGridModel) => {
          return UtilityMethods.formatDateInString(data.createdDate);
        },
      },

      {
        dataKey: "amount",
        title: "Amount",
        onCellRender: (data: RelatedPaymentsGridModel) => {
          const amount = "$" + AccountSettingsStore.formatAmount(data.amount);
          if (data.isRefund && data.success) return `(${amount})`;
          else return amount;
        },
      },
      {
        dataKey: "description",
        title: "Description",
        onCellRender: (data: RelatedPaymentsGridModel) => {
          return data.description;
        },
      },
      {
        dataKey: "paymentMethod",
        title: "Payment Method",
        onCellRender: (data: RelatedPaymentsGridModel) => {
          return data.paymentMethod ? (
            <RenderPaymentMethod paymentMethod={data.paymentMethod} />
          ) : (
            "-"
          );
        },
      },
      {
        dataKey: "paymentMethod",
        title: "Type",
        onCellRender: (data: RelatedPaymentsGridModel) => {
          return data.paymentMethod ? (
            <RenderPaymentMethodType type={data.paymentMethod.type} />
          ) : (
            "-"
          );
        },
      },
    ];
    return columns;
  };

  if (payments.length == 0)
    return (
      <>
        {upsertPaymentDetails && (
          <UpsertPaymentPopUp
            onClose={(refresh: boolean, contactId?: string) =>
              onclose(refresh, contactId)
            }
            paymentData={upsertPaymentDetails}
            countriesWithState={countriesWithState}
          />
        )}
        <PlaceholderSmallerGrid
          title="No Payments"
          icon={PaymentIcon}
          buttonCaption="New"
          disableButton={disableNewButton === true}
          buttonClick={() => {
            if (!rolesAndPermissionStore.hasManagePermission("Create")) return;
            setPaymentDetails(
              new UpsertPaymentModel({
                contact: contact,
                source: source,
                sourceId: sourceId,
                time: UtilityMethods.getHHMMFromDate(new Date()),
                amPm: UtilityMethods.getAMPMFromDate(new Date()),
              }),
            );
          }}
        />
      </>
    );

  function onclose(refresh: boolean, contactId?: string) {
    if (refresh) {
      setPaymentDetails(null);
      onUpdate && onUpdate(contactId);
    } else {
      setPaymentDetails(null);
    }
  }

  return (
    <>
      {upsertPaymentDetails && (
        <UpsertPaymentPopUp
          contactId={contact?.id}
          onClose={(refresh: boolean) => onclose(refresh)}
          paymentData={upsertPaymentDetails}
          countriesWithState={countriesWithState}
        />
      )}

      <TableGrid
        onRowClick={(data: RelatedPaymentsGridModel) =>
          !AuthService.isMindMeContactRoute &&
          navigate(`/app/payments/detail/${data.id}`)
        }
        wrapperClassName="table-with-header-btns table-header-max-height"
        tableHeader={
          <TableWithHeaderButtons
            isShowHeaderButton={true}
            totalItems={payments.length}
            icon={PaymentIcon}
            title="Payments"
            onNewButtonClick={() => {
              if (!rolesAndPermissionStore.hasManagePermission("Create"))
                return;
              setPaymentDetails(
                new UpsertPaymentModel({
                  contact: contact,
                  source: source,
                  sourceId: sourceId,
                  dueDate: new Date().toISOString(),
                  time: UtilityMethods.getHHMMFromDate(new Date()),
                  amPm: UtilityMethods.getAMPMFromDate(new Date()),
                }),
              );
            }}
          />
        }
        cellSpacing={20}
        headers={headers()}
        data={payments}
        totalItemCount={payments.length}
        name={GridNames.Payment}
        countriesWithState={countriesWithState}
      />
    </>
  );
};

export default withRouter(RelatedPaymentsGrid);
